import { CSSProperties, forwardRef, PropsWithChildren } from 'react'
import {
  Avatar,
  Box,
  Group,
  Menu,
  Text,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core'
import { TbChevronRight } from 'react-icons/tb'
import classes from './UserButton.module.css'

export interface UserButtonProps
  extends React.ComponentPropsWithoutRef<'button'> {
  image?: string
  firstName: string | null
  lastName: string | null
  email: string
}

const UserButton = forwardRef<HTMLButtonElement, UserButtonProps>(
  (
    {
      image,
      firstName,
      lastName,
      email,
      className,
      style,
      ...others
    }: UserButtonProps,
    ref,
  ) => {
    const theme = useMantineTheme()
    const buttonStyle = {
      '--user-button-color':
        theme.colorScheme === 'dark' ? '#d6dae2' : theme.black,
      '--user-button-hover-background':
        theme.colorScheme === 'dark'
          ? 'var(--cl-app-surface-subtle)'
          : theme.colors.gray[0],
      ...style,
    } as CSSProperties

    return (
      <UnstyledButton
        data-testid={'user-dropdown-button'}
        ref={ref}
        className={`${classes.button} ${className ?? ''}`}
        style={buttonStyle}
        {...others}
      >
        <Group>
          <Avatar src={image} radius="xl">
            {firstName !== null && firstName.substring(0, 1)}
            {lastName !== null && lastName.substring(0, 1)}
          </Avatar>

          <Box visibleFrom="md" style={{ flex: 1 }}>
            <Text size="xs" fw={500}>
              {firstName} {lastName}
            </Text>
            <Text c="dimmed" size="xs">
              {email}
            </Text>
          </Box>

          <TbChevronRight size={16} />
        </Group>
      </UnstyledButton>
    )
  },
)

UserButton.displayName = 'UserButton'

export const UserButtonMenu = ({
  children,
  ...user
}: PropsWithChildren<UserButtonProps>) => {
  return (
    <Group justify="center">
      <Menu
        zIndex={10}
        withArrow
        transitionProps={{
          transition: 'pop-top-right',
        }}
      >
        <Menu.Target>
          <UserButton {...user} />
        </Menu.Target>
        {children}
      </Menu>
    </Group>
  )
}
